// ============================================================================
// JARVISTRAVEL - LOCAL TRIPS STORE
// Front-end persistence for trips the Jarvis planner builds in a demo session
// (a `demo-` id), and for trips built before planner trips were saved to core (a
// `local-` id, JAR-1755), which move to core (moveLocalTrips, JAR-1756). The
// planner writes here only in a demo session; useDashboardData reads here.
// localStorage-backed so a built trip survives the onboarding -> app navigation
// and reloads.
// ============================================================================

import type { JournalTrip } from '@/app/types';
import { warnOnce } from '@/app/utils/observability';
import { recomputeDays } from '@/app/utils/fiEngine';

// Trips are stored per user (JAR-599). A single global key meant that when a
// browser crashed or the tab was killed — the three paths where the sign-out
// purge from JAR-489 never runs — the next person to sign in on that browser
// was seeded from the previous user's trips.
//
// Namespacing makes that structural rather than dependent on cleanup having
// run: a read for user B cannot address user A's rows at all.
const KEY_PREFIX = 'jarvis_local_trips:';

// The pre-namespace key. Discarded, never adopted — see discardLegacyRows.
const LEGACY_KEY = 'jarvis_local_trips';

// The owner is a PARAMETER, not module state.
//
// It used to be a module-scoped `ownerId` that App assigned during render. That
// made "App never wired the owner" and "nobody is signed in" the same value —
// both null, both producing empty reads and dropped writes — so a genuine
// init-order bug was indistinguishable from the ordinary signed-out case
// (JAR-599 M2). #161 made the dropped write observable; passing the owner makes
// it impossible to reach these functions without having said whose trips you
// mean. A caller with no user passes null deliberately, which reads as a
// decision rather than as an accident of ordering.
//
// Deliberately not read from `jarvis_user`: that key is only written by the
// legacy email/password path (api/auth.ts), so for a Clerk session — the
// primary path — it is absent, and scoping on it would put every Clerk user
// back in a single shared bucket.

/** An owner id, or null for "nobody is signed in" — stated, never inferred. */
export type TripsOwner = string | null;

const normalizeOwner = (owner: TripsOwner): string | null =>
  owner && owner.trim() ? owner.trim() : null;

/**
 * Discard pre-namespace rows. Genuinely once, and only when the app calls it —
 * never at module load.
 *
 * NOT adopted into the current user's bucket, which was the other option:
 * adopt-on-first-load would hand a crashed session's trips to whoever signs in
 * next, which is the exact leak this ticket exists to close.
 *
 * It used to run at import. That made merely importing this module destructive
 * — in a Node test, an SSR bundle, a worker, or an HMR reload — and the
 * justification (only dev and demo browsers are affected) rested on a premise
 * about deployment that this file cannot check. Deleting user-authored rows is
 * the aggressive half of discard-not-adopt, so it now happens at a point the
 * app chose, not as a side effect of an import.
 */
let legacyDiscarded = false;

/**
 * Called by App once it has chosen an identity. Was folded into
 * setLocalTripsOwner; now that the owner is a parameter there is no such call,
 * so the discard is its own named step rather than a side effect of assignment.
 */
export function discardLegacyLocalTrips(): void {
  if (legacyDiscarded) return;
  legacyDiscarded = true;
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore disabled storage */
  }
}

/** Test hook — lets a suite observe the once-only behaviour. */
export function __resetLegacyDiscard(): void {
  legacyDiscarded = false;
}

/**
 * Prefix every demo identity carries, so demo buckets are recognisable.
 *
 * The demo identity used to be the constant "demo-1", which put every demo
 * session on a browser in one bucket: the next visitor to click "try the demo"
 * was seeded from the previous visitor's trips — their destinations, names,
 * itineraries and budgets — whenever the sign-out purge had not run. That is
 * the same leak JAR-599 closed for signed-in users, still open for demo, and
 * the fix is the same one: unique per-tab ids (see demoSessionId in App.tsx),
 * so a read cannot address another session's rows at all (web-app#185 review,
 * M1).
 *
 * Abandoned demo buckets are unreachable but linger until the next
 * sign-out/exit-demo purge, which sweeps every jarvis_-prefixed key. An
 * earlier revision swept them eagerly at mint time instead; that deleted a
 * still-live sibling demo tab's persisted trips (sessionStorage is per-tab),
 * which is user-visible data loss traded against kilobytes (review round 2).
 */
export const DEMO_OWNER_PREFIX = 'demo-';

/** The key for this owner, or null when there is no owner to scope by. */
function storageKey(owner: TripsOwner): string | null {
  const id = normalizeOwner(owner);
  return id ? KEY_PREFIX + id : null;
}

export function getLocalTrips(owner: TripsOwner): JournalTrip[] {
  const key = storageKey(owner);
  // No owner means no trips — never a fallback to a shared key, or the leak
  // returns for every signed-out read.
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as JournalTrip[]).map(rerate) : [];
  } catch {
    return [];
  }
}

// A trip the planner saved before JAR-1436 stored a pace preset on every day,
// which the engine disagrees with, so its first edit re-rated every day at once.
// Re-rating on read settles that now, and changes nothing for a trip built
// since. A row that can't be re-rated comes back as stored: a throw here would
// empty the whole list, and the next add would persist the empty list.
function rerate(trip: JournalTrip): JournalTrip {
  try {
    return { ...trip, itinerary: recomputeDays(trip.itinerary) };
  } catch {
    return trip;
  }
}

function persist(owner: TripsOwner, trips: JournalTrip[]): void {
  const key = storageKey(owner);
  // No owner means the write cannot be scoped to anyone, so it is dropped —
  // never written to a shared key, which is the leak this store exists to close.
  // But the callers below still hand their in-memory list back, so a trip built
  // in this window shows in the UI and is gone on the next remount. Dropping a
  // user-authored write silently is the failure mode we refuse elsewhere, so at
  // minimum it must be observable. warnOnce dedupes: this fires per session, not
  // per keystroke.
  if (!key) {
    warnOnce('[localTrips] write dropped: no owner set; trip will not survive a reload');
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(trips));
  } catch {
    /* ignore quota / disabled storage */
  }
}

/** Add a newly built trip to the front of the list. Returns the full list. */
export function addLocalTrip(owner: TripsOwner, trip: JournalTrip): JournalTrip[] {
  const next = [trip, ...getLocalTrips(owner).filter((t) => t.id !== trip.id)];
  persist(owner, next);
  return next;
}

/** Shallow-merge an update into a stored trip by id. Returns the full list. */
export function updateLocalTrip(owner: TripsOwner, id: string, patch: Partial<JournalTrip>): JournalTrip[] {
  const next = getLocalTrips(owner).map((t) => (t.id === id ? { ...t, ...patch } : t));
  persist(owner, next);
  return next;
}

export function removeLocalTrip(owner: TripsOwner, id: string): JournalTrip[] {
  const next = getLocalTrips(owner).filter((t) => t.id !== id);
  persist(owner, next);
  return next;
}

/**
 * Removes a browser trip and its receipt scans, the one cache kept under a
 * browser trip's id outside a demo. A scan can't have reached core for a trip
 * core didn't have.
 */
export function forgetLocalTrip(owner: TripsOwner, id: string): void {
  removeLocalTrip(owner, id);
  try {
    localStorage.removeItem(`jarvis_scans_${id}`);
  } catch {
    /* storage unavailable: nothing to drop */
  }
}

/**
 * Clear the current owner's trips only — not every user's. No production
 * caller today (sign-out goes through purgeAllLocalUserData, which sweeps by
 * key prefix); kept for the planner's discard path and used by tests.
 */
export function clearLocalTrips(owner: TripsOwner): void {
  const key = storageKey(owner);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Merge backend trips with locally built (Jarvis planner) trips. API data wins
 * on id collisions (the backend copy is authoritative once a trip syncs), but
 * local-only trips are KEPT — a trip the user just built must never vanish
 * when the trips API resolves. Local drafts lead so the newest work stays on top.
 *
 * A local copy marked `movedToCoreId` (JAR-1869) is its core trip's twin, kept
 * because core refused parts of what was built for it. Core's copy wins while
 * it answers; a marked copy with no core copy on the list surfaces again, and
 * is the only place the refused parts still exist.
 */
export function mergeTrips(apiTrips: JournalTrip[], localTrips: JournalTrip[]): JournalTrip[] {
  const apiIds = new Set(apiTrips.map((t) => t.id));
  const localOnly = localTrips.filter(
    (t) => !apiIds.has(t.id) && !(t.movedToCoreId && apiIds.has(t.movedToCoreId)),
  );
  return [...localOnly, ...apiTrips];
}
